import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useTranslation } from 'react-i18next';
import type {
	DashboardUiColumnConfiguration,
	DashboardUiLocaleLabel,
	DashboardUiTableConfiguration,
	Language
} from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import {
	type ColumnLabelOverrides,
	type ColumnTableId,
	removeLanguageFromColumnLabelOverrides
} from './column-label-overrides';

export const DEFAULT_LANGUAGE_CODE = 'en';

const getEnglishLanguage = (name: string): Language => ({
	id: 0,
	code: DEFAULT_LANGUAGE_CODE,
	name
});

const getFallbackLanguage = (code: string): Language => ({
	id: 0,
	code,
	name: code
});

const getColumnLanguageCodes = (columnMap?: DashboardUiColumnConfiguration): string[] =>
	Object.keys(columnMap?.title || {});

const addTableLanguageCodes = (codes: Set<string>, tableMap: DashboardUiTableConfiguration): void => {
	Object.values(tableMap).forEach(columnMap => {
		getColumnLanguageCodes(columnMap).forEach(code => codes.add(code));
	});
};

const collectLanguageCodes = (overrides: ColumnLabelOverrides, defaultLanguageCode: string): string[] => {
	const codes = new Set<string>([defaultLanguageCode]);

	(Object.keys(overrides || {}) as ColumnTableId[]).forEach(tableId => {
		const tableMap = overrides[tableId];
		if (!tableMap) return;

		addTableLanguageCodes(codes, tableMap);
	});

	return Array.from(codes);
};

const getStoredTitleMap = (columnMap?: DashboardUiColumnConfiguration): DashboardUiLocaleLabel =>
	columnMap?.title || {};

const hasStoredLanguage = (columnMap: DashboardUiColumnConfiguration | undefined, languageCode: string): boolean =>
	languageCode in getStoredTitleMap(columnMap);

const restoreLanguageInColumnMap = (
	columnMap: DashboardUiColumnConfiguration | undefined,
	storedTitleMap: DashboardUiLocaleLabel,
	languageCode: string
): DashboardUiColumnConfiguration => ({
	...(columnMap || { title: {} }),
	title: {
		...(columnMap?.title || {}),
		[languageCode]: storedTitleMap[languageCode]
	}
});

const restoreLanguageInTableMap = (
	tableMap: DashboardUiTableConfiguration | undefined,
	storedTableMap: DashboardUiTableConfiguration,
	languageCode: string
): DashboardUiTableConfiguration => {
	const nextTableMap = { ...(tableMap || {}) };

	Object.keys(storedTableMap).forEach(columnId => {
		const storedColumnMap = storedTableMap[columnId];

		if (!hasStoredLanguage(storedColumnMap, languageCode)) return;

		nextTableMap[columnId] = restoreLanguageInColumnMap(
			nextTableMap[columnId],
			getStoredTitleMap(storedColumnMap),
			languageCode
		);
	});

	return nextTableMap;
};

const restoreLanguageFromStoredOverrides = (
	overrides: ColumnLabelOverrides,
	storedOverrides: ColumnLabelOverrides,
	languageCode: string
): ColumnLabelOverrides => {
	const next: ColumnLabelOverrides = { ...(overrides || {}) };

	(Object.keys(storedOverrides || {}) as ColumnTableId[]).forEach(tableId => {
		const storedTableMap = storedOverrides[tableId];
		if (!storedTableMap) return;

		next[tableId] = restoreLanguageInTableMap(next[tableId], storedTableMap, languageCode);
	});

	return next;
};

const createLanguagesByCodeMap = (
	availableLanguages: Language[],
	englishLanguageName: string
): Map<string, Language> => {
	const map = new Map<string, Language>();

	map.set(DEFAULT_LANGUAGE_CODE, getEnglishLanguage(englishLanguageName));
	availableLanguages.forEach(lang => {
		if (!map.has(lang.code)) {
			map.set(lang.code, lang);
		}
	});

	return map;
};

const filterLanguagesToAdd = (languagesByCode: Map<string, Language>, activeLanguageCodes: string[]): Language[] =>
	Array.from(languagesByCode.values()).filter(lang => !activeLanguageCodes.includes(lang.code));

const resolveActiveLanguages = (activeLanguageCodes: string[], languagesByCode: Map<string, Language>): Language[] =>
	activeLanguageCodes.map(code => languagesByCode.get(code) || getFallbackLanguage(code));

const getLanguageDisplayName = (languagesByCode: Map<string, Language>, languageCode: string): string =>
	languagesByCode.get(languageCode)?.name?.toUpperCase() ?? languageCode.toUpperCase();

interface UseColumnLabelsLanguagesProps {
	storedOverrides: ColumnLabelOverrides;
	setOverrides: Dispatch<SetStateAction<ColumnLabelOverrides>>;
	defaultLanguageCode: string;
}

export const useColumnLabelsLanguages = ({
	storedOverrides,
	setOverrides,
	defaultLanguageCode
}: UseColumnLabelsLanguagesProps) => {
	const { t } = useTranslation();
	const [activeLanguageCode, setActiveLanguageCode] = useState<string>(defaultLanguageCode);
	const [activeLanguageCodes, setActiveLanguageCodes] = useState<string[]>([defaultLanguageCode]);
	const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);

	useEffect(() => {
		const fetchLanguages = async () => {
			setAvailableLanguages(await getRootEnv().dashboardSdk.sdk.languages.getAll());
		};

		void fetchLanguages();
	}, []);

	useEffect(() => {
		setActiveLanguageCodes(collectLanguageCodes(storedOverrides, defaultLanguageCode));
	}, [storedOverrides, defaultLanguageCode]);

	const englishLanguageName = t('WEB_USER_INTERFACE.COLUMN_LABELS.LANGUAGE_NAME.ENGLISH');

	const allLanguagesByCode = useMemo<Map<string, Language>>(
		() => createLanguagesByCodeMap(availableLanguages, englishLanguageName),
		[availableLanguages, englishLanguageName]
	);

	const availableLanguagesToAdd = useMemo<Language[]>(
		() => filterLanguagesToAdd(allLanguagesByCode, activeLanguageCodes),
		[allLanguagesByCode, activeLanguageCodes]
	);

	const activeLanguages = useMemo<Language[]>(
		() => resolveActiveLanguages(activeLanguageCodes, allLanguagesByCode),
		[activeLanguageCodes, allLanguagesByCode]
	);

	const onAddLanguage = useCallback(
		(lang: Language) => {
			setActiveLanguageCodes(prev => (prev.includes(lang.code) ? prev : [...prev, lang.code]));
			setOverrides(prev => restoreLanguageFromStoredOverrides(prev || {}, storedOverrides, lang.code));
			setActiveLanguageCode(lang.code);
		},
		[setOverrides, storedOverrides]
	);

	const onRemoveLanguage = useCallback(
		(code: string) => {
			if (code === defaultLanguageCode) return;
			setActiveLanguageCodes(prev => prev.filter(c => c !== code));
			setOverrides(prev => removeLanguageFromColumnLabelOverrides(prev || {}, code));
			if (activeLanguageCode === code) {
				setActiveLanguageCode(defaultLanguageCode);
			}
		},
		[activeLanguageCode, defaultLanguageCode, setOverrides]
	);

	const activeLanguageName = getLanguageDisplayName(allLanguagesByCode, activeLanguageCode);

	return {
		activeLanguageCode,
		activeLanguages,
		availableLanguagesToAdd,
		activeLanguageName,
		setActiveLanguageCode,
		onAddLanguage,
		onRemoveLanguage
	};
};
